import { useStoreClock, useStorePlanningWeek } from '@gozoki/api'
import type { StoreClockAction, StorePlanningShift } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import closeIcon from '~/assets/dashboard/close.svg'
import arrowLeftIcon from '~/assets/dashboard/pointage-arrow-left.svg'
import arrowRightIcon from '~/assets/dashboard/pointage-arrow-right.svg'
import loginIcon from '~/assets/dashboard/pointage-login.svg'
import logoutIcon from '~/assets/dashboard/pointage-logout.svg'
import tagIcon from '~/assets/dashboard/pointage-tag.svg'
import { PointageCamera, type PointageCameraHandle } from './PointageCamera'

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

// `collaboratorName` = « Prénom NOM » : premier token = prénom, le reste = nom (peut être composé).
const splitName = (full: string) => {
  const parts = full.trim().split(/\s+/)
  const first = parts.shift() ?? full
  return { first, last: parts.join(' ') }
}

// Retire un éventuel préfixe département du label boutique (« 75 - PARIS » → « PARIS »), déjà
// représenté par le badge numéro du bandeau.
const cleanStoreName = (label?: string) => (label ?? '').replace(/^\d{2,3}\s*-\s*/, '')

// Une fin de créneau à minuit est stockée « 24:00 » (fin de journée exacte) ; on l'affiche « 00:00 ».
const normalizeEndTime = (time: string) => (time === '24:00' ? '00:00' : time)

// Un créneau ne se pointe qu'une fois dans chaque sens : entrée tant qu'aucune arrivée n'existe,
// sortie une fois arrivé et pas encore parti. Ces gardes reflètent les refus backend
// (`already_clocked_in`, `no_open_shift`) pour éviter un aller-retour serveur inutile.
const canClockIn = (shift: StorePlanningShift) => !shift.clockedInAt
const canClockOut = (shift: StorePlanningShift) => !!shift.clockedInAt && !shift.clockedOutAt
const defaultAction = (shift: StorePlanningShift): StoreClockAction =>
  canClockOut(shift) ? 'clock-out' : 'clock-in'

// Sous-titre du bouton Entrée/Sortie : heure déjà pointée (sens verrouillé), sinon heure planifiée,
// ou l'invite générique tant qu'aucun profil n'est choisi.
const actionSubtitle = (shift: StorePlanningShift | null, isIn: boolean): string => {
  if (!shift) return `Je valide ${isIn ? 'mon entrée' : 'ma sortie'}`
  if (isIn && shift.clockedInAt) return `Déjà pointée à ${shift.clockedInAt}`
  if (!isIn && shift.clockedOutAt) return `Déjà pointée à ${shift.clockedOutAt}`
  return `Prévu à ${isIn ? shift.startTime : normalizeEndTime(shift.endTime)}`
}

const ERROR_MESSAGES: Record<string, string> = {
  already_clocked_in: 'Ce collaborateur a déjà pointé son arrivée.',
  no_open_shift: 'Aucune arrivée à clôturer pour ce collaborateur.',
  collaborator_not_scheduled: "Ce collaborateur n'est pas planifié aujourd'hui.",
}

function Avatar({ url, name, size = 48 }: { url: string | null; name: string; size?: number }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        aria-hidden
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-[#cee3c4] font-brand font-semibold text-[#152013]"
      style={{ width: size, height: size, fontSize: size / 2.4 }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  )
}

const VDivider = () => <span className="h-[40px] w-px shrink-0 bg-[#e3e1d8]" />
const HDivider = () => <span className="h-px w-full shrink-0 bg-[rgba(0,0,0,0.06)]" />

function Header({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="flex w-full items-center gap-[12px]">
        <span className="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#f9f8f1]">
          <img src={tagIcon} alt="" aria-hidden className="size-[24px]" />
        </span>
        <h2 className="flex-1 font-brand text-[22px] leading-[26px] font-semibold text-[#4f6a47]">
          Faire mon pointage
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="shrink-0 cursor-pointer p-[2px] -m-[2px]"
        >
          <span className="flex size-[40px] items-center justify-center rounded-[8px] bg-[#f9f8f1]">
            <img src={closeIcon} alt="" aria-hidden className="size-[16px]" />
          </span>
        </button>
      </div>
      <HDivider />
    </>
  )
}

function StepLabel({ n }: { n: 1 | 2 }) {
  return (
    <div className="flex w-full items-center gap-[10px]">
      <span className="shrink-0 font-brand text-[28px] leading-[32px] font-extrabold text-[#4f6a47]">
        Étape {n} / 2
      </span>
      <span className="h-px flex-1 bg-[rgba(0,0,0,0.06)]" />
    </div>
  )
}

/**
 * Wizard de pointage kiosk (Figma 11308:38967 / 11393:41464), ouvert depuis « Faire mon pointage ».
 * 2 étapes : (1) bandeau boutique + choix du profil (planning du jour) + type Entrée/Sortie, puis
 * (2) vérification d'identité — la caméra témoin est figée d'un tap dans l'ovale (« Identité
 * validée »), et la validation écrit un `WorkShiftClock` (POST /store/clock). Overlay `fixed` posé
 * sur `document.body`, hors du canvas mis à l'échelle.
 */
export function PointageModal({
  storeLabel,
  storeAddress,
  storeDepartmentCode,
  onClose,
}: {
  storeLabel?: string
  storeAddress?: string | null
  storeDepartmentCode?: string | null
  onClose: () => void
}) {
  const cameraRef = useRef<PointageCameraHandle>(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [selected, setSelected] = useState<StorePlanningShift | null>(null)
  const [action, setAction] = useState<StoreClockAction>('clock-in')
  const [photo, setPhoto] = useState<Blob | null>(null)
  // Instant figé au moment de la capture : la validation ne doit pas dériver avec l'horloge live.
  const [capturedAt, setCapturedAt] = useState<DateTime | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [now, setNow] = useState(() => DateTime.now().setZone('Europe/Paris'))

  const { data: planning } = useStorePlanningWeek()
  const { mutate, isPending } = useStoreClock()

  useEffect(() => {
    const id = setInterval(() => setNow(DateTime.now().setZone('Europe/Paris')), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const today = planning?.days.find((day) => day.isToday)
  const shifts = today?.shifts ?? []

  const dateLabel = `${capitalize(now.setLocale('fr').toFormat('ccc'))} ${now.toFormat('d')} ${capitalize(now.setLocale('fr').toFormat('LLLL'))}`
  const timeLabel = now.toFormat('HH:mm')
  const displayAt = capturedAt ?? now

  const pickCollaborator = (shift: StorePlanningShift) => {
    setSelected(shift)
    // Défaut intelligent : un shift ouvert (pointé entrée, pas sortie) → Départ, sinon Arrivée.
    setAction(defaultAction(shift))
  }

  const goToStep2 = () => {
    if (!selected) return
    setPhoto(null)
    setCapturedAt(null)
    setErrorMessage(null)
    setStep(2)
  }

  const goBack = () => {
    setPhoto(null)
    setCapturedAt(null)
    setErrorMessage(null)
    setStep(1)
  }

  const capture = async () => {
    const blob = await cameraRef.current?.takePhoto()
    if (!blob) {
      setErrorMessage("La photo n'a pas pu être prise, réessayez.")
      return
    }
    setPhoto(blob)
    setCapturedAt(now)
    setErrorMessage(null)
  }

  const validate = () => {
    if (!selected || !photo) return
    setErrorMessage(null)
    mutate(
      {
        collaboratorId: selected.collaboratorId,
        workShiftId: selected.id,
        action,
        temoinFile: photo,
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          const code = (error as { response?: { data?: { error?: string } } }).response?.data?.error
          setErrorMessage((code && ERROR_MESSAGES[code]) || 'Erreur lors du pointage, réessayez.')
        },
      },
    )
  }

  const selectedName = selected ? splitName(selected.collaboratorName) : null
  const validateLabel = `Valider mon pointage ${action === 'clock-in' ? "d'entrée" : 'de sortie'}`
  // Le sens choisi doit encore être ouvert : sinon le créneau est déjà pointé dans ce sens.
  const canProceed =
    selected != null && (action === 'clock-in' ? canClockIn(selected) : canClockOut(selected))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,44,26,0.25)] p-[24px]"
      role="dialog"
      aria-modal="true"
      aria-label="Pointage"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-[910px] max-w-full flex-col gap-[24px] overflow-y-auto kiosk-scroll-y rounded-[24px] bg-[#f0ede2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <Header onClose={onClose} />

        {step === 1 ? (
          <>
            {/* Bandeau boutique */}
            <div className="flex w-full items-center gap-[16px] rounded-[12px] bg-[#f9f8f1] p-[24px]">
              {storeDepartmentCode && (
                <span className="flex size-[48px] shrink-0 items-center justify-center rounded-[8px] bg-[#f0ede2] font-brand text-[22px] font-extrabold text-[#152013]">
                  {storeDepartmentCode}
                </span>
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                {storeAddress && (
                  <span className="truncate font-sans text-[14px] leading-[16px] font-semibold text-[#6a9560]">
                    {storeAddress}
                  </span>
                )}
                <span className="truncate font-brand text-[22px] leading-[26px] font-extrabold text-[#152013] uppercase">
                  {cleanStoreName(storeLabel) || 'Boutique'}
                </span>
              </div>
              <VDivider />
              <div className="flex shrink-0 flex-col items-end gap-[8px]">
                <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#7c867d]">
                  Date
                </span>
                <span className="font-brand text-[22px] leading-[26px] font-extrabold text-[#6a9560]">
                  {dateLabel}
                </span>
              </div>
              <VDivider />
              <div className="flex shrink-0 flex-col items-end gap-[8px]">
                <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#7c867d]">
                  Heure
                </span>
                <span className="font-brand text-[22px] leading-[26px] font-extrabold text-[#4f6a47]">
                  {timeLabel}
                </span>
              </div>
            </div>

            <StepLabel n={1} />

            {/* Sélection du profil */}
            <div className="flex w-full flex-col gap-[10px]">
              <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#58655b]">
                Sélectionner votre profil
              </span>
              {shifts.length === 0 ? (
                <div className="flex h-[120px] items-center justify-center rounded-[12px] border border-[rgba(0,0,0,0.06)] bg-[#f9f8f1] text-center font-sans text-[14px] text-[#7c867d]">
                  Aucun collaborateur planifié aujourd'hui
                </div>
              ) : (
                <div className="flex w-full gap-[8px] overflow-x-auto kiosk-scroll-x">
                  {shifts.map((shift) => {
                    const isActive = selected?.id === shift.id
                    const { first, last } = splitName(shift.collaboratorName)
                    return (
                      <button
                        key={shift.id}
                        type="button"
                        onClick={() => pickCollaborator(shift)}
                        className={cn(
                          'flex min-w-[150px] flex-1 cursor-pointer flex-col items-center gap-[10px] rounded-[12px] border p-[16px]',
                          isActive
                            ? 'border-[#b8d9a8] bg-[#e4eddb]'
                            : 'border-[rgba(0,0,0,0.06)] bg-[#f9f8f1]',
                        )}
                      >
                        <Avatar url={shift.profilePictureUrl} name={first} size={74} />
                        <span className="flex flex-col items-center gap-[4px]">
                          <span className="font-brand text-[22px] leading-[26px] font-semibold text-[#4f6a47]">
                            {first}
                          </span>
                          <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#58655b] uppercase">
                            {last}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Sélection du type de pointage */}
            <div className="flex w-full flex-col gap-[10px]">
              <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#58655b]">
                Sélectionner votre type de pointage
              </span>
              <div className="flex w-full gap-[8px]">
                {(['clock-in', 'clock-out'] as const).map((value) => {
                  const isActive = action === value
                  const isIn = value === 'clock-in'
                  const isAllowed =
                    selected != null && (isIn ? canClockIn(selected) : canClockOut(selected))
                  const subtitle = actionSubtitle(selected, isIn)
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAction(value)}
                      disabled={!isAllowed}
                      className={cn(
                        'flex flex-1 cursor-pointer items-center gap-[24px] rounded-[12px] border p-[24px] disabled:cursor-not-allowed',
                        isActive
                          ? 'border-[#b8d9a8] bg-[#e4eddb]'
                          : 'border-transparent bg-[#f9f8f1]',
                      )}
                    >
                      <span
                        className={cn(
                          'flex shrink-0 items-center justify-center rounded-[8px] p-[12px]',
                          isActive ? 'bg-[#cee3c4]' : 'bg-[#f0ede2]',
                        )}
                      >
                        <img
                          src={isIn ? loginIcon : logoutIcon}
                          alt=""
                          aria-hidden
                          className="size-[24px]"
                        />
                      </span>
                      <span className="flex min-w-0 flex-col items-start gap-[4px]">
                        <span className="font-brand text-[22px] leading-[26px] font-semibold text-[#4f6a47]">
                          {isIn ? 'Entrée' : 'Sortie'}
                        </span>
                        <span className="flex items-center gap-[8px]">
                          <img src={arrowRightIcon} alt="" aria-hidden className="size-[20px]" />
                          <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#58655b]">
                            {subtitle}
                          </span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <HDivider />

            <button
              type="button"
              onClick={goToStep2}
              disabled={!canProceed}
              className="h-[52px] w-full cursor-pointer rounded-[8px] bg-[#6a9560] font-sans text-[14px] font-semibold text-[#f9f8f1] disabled:cursor-not-allowed disabled:bg-[#a4c494]"
            >
              Vérifier mon identité
            </button>
          </>
        ) : (
          selected &&
          selectedName && (
            <>
              {/* Bandeau profil sélectionné */}
              <div className="flex w-full items-center gap-[16px] rounded-[12px] bg-[#f9f8f1] p-[24px]">
                <button
                  type="button"
                  onClick={goBack}
                  aria-label="Retour"
                  className="flex size-[48px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] bg-[#f0ede2]"
                >
                  <img src={arrowLeftIcon} alt="" aria-hidden className="size-[16px]" />
                </button>
                <VDivider />
                <Avatar url={selected.profilePictureUrl} name={selectedName.first} size={48} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="font-sans text-[12px] font-semibold text-[#7c867d]">
                    Profil sélectionné
                  </span>
                  <span className="truncate font-brand text-[22px] leading-[26px] font-extrabold text-[#152013] uppercase">
                    {selected.collaboratorName}
                  </span>
                </div>
                <VDivider />
                <div className="flex shrink-0 flex-col items-end gap-[8px]">
                  <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#7c867d]">
                    Type
                  </span>
                  <span className="font-brand text-[22px] leading-[26px] font-extrabold text-[#6a9560]">
                    {action === 'clock-in' ? 'Entrée' : 'Sortie'}
                  </span>
                </div>
                <VDivider />
                <div className="flex shrink-0 flex-col items-end gap-[8px]">
                  <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#7c867d]">
                    Date
                  </span>
                  <span className="font-brand text-[22px] leading-[26px] font-extrabold text-[#6a9560]">
                    {dateLabel}
                  </span>
                </div>
                <VDivider />
                <div className="flex shrink-0 flex-col items-end gap-[8px]">
                  <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#7c867d]">
                    Heure
                  </span>
                  <span className="font-brand text-[22px] leading-[26px] font-extrabold text-[#4f6a47]">
                    {displayAt.toFormat('HH:mm')}
                  </span>
                </div>
              </div>

              <StepLabel n={2} />

              {/* Vérification d'identité (caméra témoin) */}
              <div className="flex w-full flex-col gap-[10px]">
                <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#58655b]">
                  Vérification de votre identité
                </span>
                <PointageCamera ref={cameraRef} captured={!!photo} onCapture={capture} />
              </div>

              {errorMessage && (
                <span className="font-sans text-[12px] text-[#F25757]">{errorMessage}</span>
              )}

              <HDivider />

              <button
                type="button"
                onClick={validate}
                disabled={!photo || isPending}
                className="h-[52px] w-full cursor-pointer rounded-[8px] bg-[#6a9560] font-sans text-[14px] font-semibold text-[#f9f8f1] disabled:cursor-not-allowed disabled:bg-[#a4c494]"
              >
                {isPending ? '…' : validateLabel}
              </button>
            </>
          )
        )}
      </div>
    </div>
  )
}
